import { useState, useCallback } from 'react';

interface ValidationState {
  [key: string]: string | null;
}

interface UseFormValidationReturn {
  errors: ValidationState;
  validateField: (field: string, value: string, validator: (value: string) => string | null) => void;
  validateFieldWithDependency: (
    field: string, 
    value: string, 
    validator: (value: string, dependency?: string) => string | null,
    dependency?: string
  ) => void;
  clearError: (field: string) => void;
  clearAllErrors: () => void;
  hasErrors: boolean;
}

export const useFormValidation = (): UseFormValidationReturn => {
  const [errors, setErrors] = useState<ValidationState>({});

  const validateField = useCallback((field: string, value: string, validator: (value: string) => string | null) => {
    const error = validator(value);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  }, []);

  const validateFieldWithDependency = useCallback((
    field: string, 
    value: string, 
    validator: (value: string, dependency?: string) => string | null,
    dependency?: string
  ) => {
    const error = validator(value, dependency);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  const hasErrors = Object.values(errors).some(error => error !== null);

  return {
    errors,
    validateField,
    validateFieldWithDependency,
    clearError,
    clearAllErrors,
    hasErrors,
  };
};
